import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { ArrowIcon } from 'assets';
import { AuthHeading, Button, Input, Spinner } from 'view/components';
import { AuthFormsFooter } from 'view/pages/auth/components';
import { useResetPassword } from 'view/pages/auth/hooks/useResetPassword';
import {
  AuthHeadingImageTypeEnums,
  getCommonPasswordValidationRules,
  parseError,
} from 'view/pages/auth/helpers';
import * as CS from 'view/pages/auth/commonStyled';

interface IFormInput {
  new_password: string;
  confirm_password: string;
}

const ResetPasswordFormFields: FC<{ isLoading: boolean }> = ({ isLoading }) => {
  const {
    watch,
    trigger,
    clearErrors,
    formState: { errors },
  } = useFormContext<IFormInput>();
  const newPasswordValue = watch('new_password');
  const confirmPasswordValue = watch('confirm_password');

  useEffect(() => {
    if (newPasswordValue || confirmPasswordValue) {
      trigger('new_password').then();
      trigger('confirm_password').then();

      if (newPasswordValue === confirmPasswordValue) {
        clearErrors(['new_password', 'confirm_password']);
      }
    }
  }, [newPasswordValue, confirmPasswordValue, trigger, clearErrors]);

  const isDisabled = !!errors.new_password || !!errors.confirm_password || isLoading;

  return (
    <>
      <Input
        name="new_password"
        label="New Password"
        type="password"
        rules={{
          ...getCommonPasswordValidationRules('Password'),
          validate: (value: string) => {
            return value === confirmPasswordValue || 'Passwords do not match';
          },
        }}
        parseError={parseError}
      />
      <Input
        name="confirm_password"
        label="Confirm Password"
        type="password"
        rules={{
          ...getCommonPasswordValidationRules('Password'),
          validate: (value: string) => {
            return value === newPasswordValue || 'Passwords do not match';
          },
        }}
        parseError={parseError}
      />
      <CS.FormWrapperMain component="div">
        <Button
          variant="contained"
          type="submit"
          text="Reset Password"
          endIcon={
            isLoading ? (
              <Spinner disabled={isDisabled} variant="contained" />
            ) : (
              <ArrowIcon rotate="right" color="white" />
            )
          }
          disabled={isDisabled}
        />
      </CS.FormWrapperMain>
    </>
  );
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();

  const params = new URLSearchParams(window.location.search);
  const hash: string | null = params.get('hash');

  const { isLoading, resetPassword } = useResetPassword();

  useEffect(() => {
    if (!hash) {
      navigate('/auth/login', { replace: true });
    }
  }, [hash]);

  const onSubmit = (formData: IFormInput) => {
    resetPassword({ password: formData.new_password, hash });
  };

  return (
    <CS.Wrapper>
      {/* <CS.BackButtonBox>
        <BackButton text="Back to Authorization" fallbackUrl="/auth/onboarding" />
      </CS.BackButtonBox> */}
      <CS.AuthMainBox component="section">
        <AuthHeading
          title="Set new password"
          subTitle={
            'Please enter a new password it may contain Latin letters, \n numbers and some special characters. Password should \n be at least 8 characters long.'
          }
          imageType={AuthHeadingImageTypeEnums.LINE}
        />
        <CS.FormWrapper component="section">
          <FormContainer
            defaultValues={{ new_password: '', confirm_password: '' }}
            onSuccess={onSubmit}
          >
            <ResetPasswordFormFields isLoading={isLoading} />
          </FormContainer>
          <AuthFormsFooter
            title="New to Injobnito?"
            hrefTo="/auth/registration"
            hrefText="Join Now"
          />
        </CS.FormWrapper>
      </CS.AuthMainBox>
    </CS.Wrapper>
  );
};

export default ResetPasswordPage;
